import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import Input from 'components/shared/form/Input';
import StandardModal from 'components/shared/StandardModal';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { updateJobNotificationTitle } from 'lib/aiJobNotificationHelpers';
import Summarization from 'models/Summarization';
import { summarizationQueryParams, updateSummarization } from 'services/api/summarizations';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

export type EditSummaryTitleModalProps = {
  summarization: Summarization;
  onCancel: () => void;
};

export default function EditSummaryTitleModal({ summarization, onCancel }: EditSummaryTitleModalProps) {
  const [title, setTitle] = useState(summarization.title);
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate: updateSummary, isPending } = useMutateWithToast({
    mutationFn: updateSummarization,
  });

  const handleUpdate = () => {
    updateSummary(
      { ...summarizationQueryParams.show(summarization.id), title },
      {
        onSuccess: () => {
          addToast({ text: 'Summary title was successfully updated!' });
          updateJobNotificationTitle(summarization.id, title);
          invalidateQueries({ summarizations: true });
          onCancel();
        },
      }
    );
  };

  const isUpdateDisabled = !title.trim() || isPending;

  return (
    <StandardModal onCancel={onCancel} title='Edit Summary Title'>
      <Content>
        <BodySmall>Summary Title</BodySmall>
        <Input
          type='text'
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder='Enter summary title'
          disabled={isPending}
        />
      </Content>
      <StandardModal.Actions>
        <Button variant='ghost' onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleUpdate} loading={isPending} disabled={isUpdateDisabled}>
          Update
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Content = styled.div`
  margin: 16px 0 40px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
